import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getBrandName } from '../utility/branding';

/**
 * Route-to-title mapping for automatic title generation
 * Maps pathname patterns to user-friendly titles
 */
const ROUTE_TITLES = {
  '/dashboard': 'Dashboard',
  '/calendar': 'Calendar',
  '/clients': 'Clients',
  '/clients/': 'Client Details', // For dynamic routes like /clients/123
  '/services': 'Services',
  '/availability': 'Availability',
  '/settings': 'Settings',
  '/settings/': 'Settings', // For dynamic routes like /settings/profile
  '/booking-page': 'Booking Page',
  '/booking-page/': 'Booking Page', // For dynamic routes like /booking-page/customize
  '/signin': 'Sign In',
  '/signup': 'Sign Up',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/verify-email-change': 'Verify Email Change',
  '/book/': 'Book Appointment', // For public booking pages
  '/manage-booking/': 'Manage Booking',
  '/404': 'Page Not Found',
};

/**
 * Custom hook for dynamic document title management
 *
 * @param {string} customTitle - Optional custom title to override automatic route-based title
 * @param {string} suffix - Optional suffix (defaults to getBrandName())
 *
 * Usage:
 * // Automatic route-based title
 * useDocumentTitle();
 *
 * // Custom title
 * useDocumentTitle("Custom Page Title");
 *
 * // Custom title with different suffix
 * useDocumentTitle("Custom Title", "My App");
 */
const useDocumentTitle = (customTitle = null, suffix = getBrandName()) => {
  const location = useLocation();
  const previousTitle = useRef(document.title);

  useEffect(() => {
    // Store the previous title when component mounts
    if (!previousTitle.current) {
      previousTitle.current = document.title;
    }

    let newTitle;

    if (customTitle) {
      // Use custom title if provided
      newTitle = `${customTitle} | ${suffix}`;
    } else {
      // Generate title from route
      const routeTitle = getRouteTitleFromPath(location.pathname);
      newTitle = routeTitle ? `${routeTitle} | ${suffix}` : suffix;
    }

    // Update document title
    document.title = newTitle;

    // Cleanup: restore previous title when component unmounts
    return () => {
      if (previousTitle.current) {
        document.title = previousTitle.current;
      }
    };
  }, [customTitle, suffix, location.pathname]);
};

/**
 * Helper function to determine title from pathname
 * Handles both exact matches and dynamic routes
 */
const getRouteTitleFromPath = (pathname) => {
  // Check for exact match first
  if (ROUTE_TITLES[pathname]) {
    return ROUTE_TITLES[pathname];
  }

  // Check for partial matches (dynamic routes)
  for (const route in ROUTE_TITLES) {
    if (route.endsWith('/') && pathname.startsWith(route)) {
      return ROUTE_TITLES[route];
    }
  }

  // Default fallback - try to create title from path segments
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0) {
    const lastSegment = segments[segments.length - 1];
    // Capitalize first letter and replace hyphens with spaces
    return lastSegment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return null;
};

export default useDocumentTitle;